import React, { forwardRef, RefObject } from 'react';
import styled, { css } from 'styled-components';
import colors from '../common/theme/colors';
import constants from '../common/theme/constants';
import KitErrorMessage from './KitErrorMessage';
import KitIcon from './KitIcon';

export interface KitCheckProps {
  className?: string;
  id?: string;
  checked?: boolean;
  disabled?: boolean;
  error?: string;
  label?: string;
  labelDataQaId?: string;
  name?: string;
  required?: boolean;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const CheckboxLabel = styled.label<{ disabled?: boolean; error?: boolean }>`
  && {
    cursor: pointer;
    user-select: none;
    font-family: ${({ theme }) => theme.font.fontFamily};
    font-weight: ${constants.fontWeight.normal};
    box-sizing: border-box;
    font-size: ${constants.fontSize.text_14}rem;
    color: ${({ theme }) => theme.checkbox.label};
    display: flex;
    align-items: center;
    position: relative;

    ${(props) =>
      props.disabled &&
      css`
        cursor: default;
        color: ${colors.gray_30};
      `}
    ${(props) =>
      props.error &&
      css`
        color: ${({ theme }) => theme.error};
      `}
  }
`;

const Input = styled.input.attrs((props: KitCheckProps) => ({
  type: 'checkbox',
  id: props.id,
  checked: props.checked,
  disabled: props.disabled,
  error: props.error,
}))`
  && {
    text-align: center;
    appearance: none;
    cursor: pointer;
    border: 1px solid ${(props) => (props.disabled ? colors.gray_30 : colors.gray_40)};
    border-radius: 2px;
    margin-right: ${constants.spacing.s}rem;
    margin-left: 0;
    width: ${constants.fontSize.base}rem;
    height: ${constants.fontSize.base}rem;
    min-width: ${constants.fontSize.base}rem;
    min-height: ${constants.fontSize.base}rem;

    &:hover {
      border-color: ${({ theme }) => theme.checkbox.color};
    }

    &:focus {
      outline-offset: 2px;
      outline: 2px solid ${({ theme }) => theme.focus};
    }

    &:checked {
      background: ${({ theme }) => theme.checkbox.color};
      border-color: ${({ theme }) => theme.checkbox.color};
    }

    &:checked:disabled,
    &:checked:disabled:hover,
    &:disabled:hover {
      border-color: ${colors.gray_40};
    }

    & + div {
      min-width: auto;
      max-width: none;
    }

    &:checked + div > svg {
      opacity: 1;
      transition: opacity 0.3s;
      fill: ${(props) => (props.disabled ? colors.gray_30 : colors.white)};
    }

    &:not(:checked) + div > svg {
      opacity: 0;
      transition: opacity 0.3s;
      fill: ${(props) => (props.disabled ? colors.gray_30 : colors.white)};
    }

    &:disabled {
      cursor: default;
      background: ${colors.gray_20};
    }

    ${(props) =>
      props.error &&
      css`
        border-color: ${({ theme }) => theme.error};
        & :focus,
        :hover {
          border-color: ${({ theme }) => theme.error};
        }
      `}
  }
`;

const StyledCheck = styled(KitIcon)`
  position: absolute;
  && {
    margin: 0.1rem;
    width: ${constants.fontSize.p}rem;
    height: ${constants.fontSize.p}rem;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${constants.spacing.absolute.xs}px;
`;

const KitCheck = forwardRef((props: KitCheckProps, ref: RefObject<HTMLInputElement>) => {
  const { required, id, label, disabled, error } = props;
  const labelOutput = required && label ? `${label} * ` : label;

  let errorMessageId;
  if ((id || labelOutput) && error) {
    errorMessageId = id ? `${id}_error` : `${labelOutput.split(' ').join('')}_error`;
  }

  return (
    <Wrapper>
      <CheckboxLabel disabled={disabled} error={!!error} data-qa-id={props.labelDataQaId}>
        <Input
          {...props}
          aria-required={required}
          aria-invalid={!!error}
          aria-describedby={errorMessageId}
          ref={ref}
        />
        <StyledCheck icon='check' aria-hidden />
        {labelOutput}
      </CheckboxLabel>
      {error && (
        <KitErrorMessage id={errorMessageId} isError={!!error}>
          {error}
        </KitErrorMessage>
      )}
    </Wrapper>
  );
});

export default KitCheck;
